import React, { useEffect, useState } from 'react';
import '../../css/Cart.css';
import { getAuth } from 'firebase/auth';
import { getFirestore, getDoc, doc, updateDoc, increment, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

/* global payhere */
function Cart() {
    const [cartItems, setCartItems] = useState([]);
    const firestore = getFirestore();
    const navigate = useNavigate();
    const auth = getAuth();

    const handlePayment = async () => {

        try {

            const user = auth.currentUser;
            if (!user) {
                alert("You must be logged in.");
                return;
            }

            const userDoc = await getDoc(doc(firestore, "users", user.uid));
            if (!userDoc.exists()) {
                alert("User details not found!");
                return;
            }

            const data = userDoc.data();

            const orderId = `Order-${Date.now()}`;

            // Simulating a payment process
            const response = await axios.post('https://us-central1-prettylovecolombo-f644d.cloudfunctions.net/api/generate-hash', {
                merchant_id: '1228388',
                order_id: orderId,
                amount: total.toFixed(2),
                currency: 'LKR',
            });

            const { hash } = response.data;

            const payment = {
                sandbox: true,
                merchant_id: '1228388',
                return_url: 'http://prettylovecolombo.com/success',
                cancel_url: 'http://prettylovecolombo.com/cancel',
                notify_url: 'https://us-central1-prettylovecolombo-f644d.cloudfunctions.net/api/notify',
                order_id: orderId,
                items: cartItems,
                amount: total.toFixed(2),
                currency: 'LKR',
                hash,
                first_name: data.firstName,
                last_name: data.lastName,
                email: data.email,
                phone: data.phone,
                address: data.address1 + data.address2,
                city: data.city,
                country: 'Sri Lanka',
            };

            payhere.startPayment(payment);

            payhere.onCompleted = async function () {
                try {
                    const db = getFirestore();
                    const user = auth.currentUser;

                    if (!user) {
                        alert("You must be logged in to complete the order.");
                        return;
                    }

                    // ✅ Always fetch latest customer details before saving order
                    const userDoc = await getDoc(doc(db, "users", user.uid));
                    let customerDetails = {};
                    if (userDoc.exists()) {
                        const data = userDoc.data();
                        customerDetails = {
                            uid: user.uid,
                            firstName: data.firstName || '',
                            lastName: data.lastName || '',
                            email: data.email || '',
                            phone: data.phone || '',
                            address1: data.address1 || '',
                            address2: data.address2 || '',
                            city: data.city || ''
                        };
                    }

                    // 🔹 Update stock for each purchased item
                    for (const item of cartItems) {
                        const productRef = doc(db, "products", item.id);
                        await updateDoc(productRef, {
                            stockQty: increment(-item.quantity)
                        });
                    }

                    // 🔹 Save order
                    const orderData = {
                        orderId: orderId,
                        items: cartItems,
                        subtotal: subtotal,
                        shipping: shipping,
                        total: total,
                        customer: customerDetails,
                        status: "paid",
                        createdAt: serverTimestamp()
                    };

                    await addDoc(collection(db, "users", user.uid, "orders"), orderData);

                    const purchasedItems = [...cartItems];

                    // 🔹 Clear cart
                    localStorage.removeItem("cart");
                    setCartItems([]);

                    // 🔹 Navigate
                    navigate('/order-confirmation', {
                        state: {
                            message: 'Thank you for your purchase!',
                            orderId: orderId,
                            amount: total,
                            items: purchasedItems
                        }
                    });

                } catch (err) {
                    console.error("Error saving order:", err);
                    alert("Payment succeeded but failed to save order. Please contact support.");
                }
            };

        } catch (error) {
            console.error('Error initiating payment:', error);
            alert(`Error occurred during payment: ${error}`);
        }

    };

    useEffect(() => {
        const storedCart = JSON.parse(localStorage.getItem('cart')) || [];
        setCartItems(storedCart);
    }, []);

    const handleQuantityChange = (index, newQty) => {
        const updatedCart = [...cartItems];
        updatedCart[index].quantity = parseInt(newQty);
        setCartItems(updatedCart);
        localStorage.setItem('cart', JSON.stringify(updatedCart));
    };

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = 500;
    const total = subtotal + shipping;

    // 🗑️ Handle item removal
    const handleRemoveItem = (index) => {
        const updatedCart = cartItems.filter((_, i) => i !== index);
        setCartItems(updatedCart);
        localStorage.setItem('cart', JSON.stringify(updatedCart));
    };

    return (
        <div className="cart-container">
            <table className="cart-table">
                <thead>
                    <tr>
                        <th>Product</th>
                        <th>Price</th>
                        <th>Quantity</th>
                        <th>Subtotal</th>
                    </tr>
                </thead>
                <tbody>
                    {cartItems.length === 0 ? (
                        <tr><td colSpan="4" className="cart-empty">Cart is empty</td></tr>
                    ) : (
                        cartItems.map((item, index) => (
                            <tr key={item.id}>
                                <td className="product-info">
                                    <img src={item.image} alt={item.title} />
                                    <span>{item.title}</span>
                                </td>
                                <td className='product-price'>Rs.{item.price.toLocaleString('en-LK')}</td>
                                <td>
                                    <input
                                        type="number"
                                        min="1"
                                        value={item.quantity}
                                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                                    />
                                </td>
                                <td className='product-subtotal'>Rs.{(item.price * item.quantity).toLocaleString('en-LK')}</td>
                                <td>
                                    <button
                                        className="delete-btn"
                                        onClick={() => handleRemoveItem(index)}
                                    >
                                        ❌
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            <div className="cart-summary">
                <div className="summary-box">
                    <p className='subtotal'><strong>Subtotal:</strong> Rs.{subtotal.toLocaleString('en-LK')}</p>
                    <p className='shipping'><strong>Delivery Charge:</strong> Rs.{shipping}</p>
                    <p className='total'><strong>Total:</strong> Rs.{total.toLocaleString('en-LK')}</p>
                    <button className="checkout-btn" onClick={handlePayment}>Proceed to Checkout</button>
                </div>
            </div>
        </div>
    );
}

export default Cart;
